/**
 * Magniberg tabs: a minimal accessible tab switcher.
 *
 * Markup: buttons with role="tab" and aria-controls pointing at elements with
 * role="tabpanel". The selected tab has aria-selected="true"; other panels are hidden.
 * Arrow keys, Home and End move between tabs.
 */
class MbTabs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @param {HTMLElement} selected */
  select(selected) {
    for (const tab of this.tabs) {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !isSelected;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement && this.contains(tab)) this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(current instanceof HTMLElement)) return;

    const tabs = this.tabs;
    const index = tabs.indexOf(current);
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = tabs[next];
    if (!target) return;

    this.select(target);
    target.focus();
  };
}

if (!customElements.get('mb-tabs')) {
  customElements.define('mb-tabs', MbTabs);
}
